import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import getMenuItems from './menuItems.js'
import { HamburgerIcon, SearchIcon, Cross2Icon } from '../icons'
import { PlayerMenu, Login } from '..'
import StvLogo from './StvLogo'
import makeIconLink from './makeIconLink'
import styles from './styles'
import { setToggleState } from '../../actions'
import { OUT_OF_REGION_LOGGED_IN_GROUPTOKEN } from '../../constants'
import SponsorLogo from './SponsorLogo.js'
import { trackEvent } from '../../utils/dtmHelpers'
import Avatar from '../Avatar'

const HamburgerLink = makeIconLink(HamburgerIcon)
const SearchLink = makeIconLink(SearchIcon, 25, 24)
const CloseSearchLink = makeIconLink(Cross2Icon)

const scriptJson = {
  '@context': 'http://schema.org',
  '@type': 'WebSite',
  name: 'STV Player',
  url: 'https://player.stv.tv/',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://player.stv.tv/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
}

const PlayerHeader = (props) => {
  const [state, setState] = useState({
    menuIsActive: false,
  })
  const [heroInView, setHeroInView] = useState(true)
  const [accountMenuActive, setAccountMenuActive] = useState(false)
  const dispatch = useDispatch()
  const playerHeadReducer = useSelector(playerState => playerState.playerHeader)
  const region = props.groupToken === OUT_OF_REGION_LOGGED_IN_GROUPTOKEN ? 'rouk' : 'stvRegion'
  const activePage = props.currentUrl.split('/')[3]
  let menuItems = getMenuItems(region)

  if (props.isLoading !== null && !props.currentlySubscribed && props.loginEnabled) {
    menuItems = [
      ...menuItems,
      {
        title: `${props.isLoggedIn ? 'Upgrade' : 'Subscribe'} to STV Player+`,
        url: `https://player.stv.tv/player-plus`,
      },
    ]
  }

  useEffect(() => {
    window.addEventListener('click', checkClick)

    checkVisible()
    window.addEventListener('click', checkClick)
    window.addEventListener('scroll', checkVisible)

    return () => {
      window.removeEventListener('click', checkClick)
      window.removeEventListener('scroll', checkVisible)
    }
  })
  const checkClick = () => {
    const isLiveDropdown = document?.querySelector('.live-tv-container')
    const element = document.getElementById('player-header')
    if (isLiveDropdown) {
      element.classList.add('hero-invisible')
    } else {
      element.classList.remove('hero-invisible')
    }
  }
  useEffect(() => {
    const closeAccountMenu = () => {
      if (accountMenuActive) {
        setAccountMenuActive(false)
      }
    }

    window.addEventListener('scroll', closeAccountMenu)

    return () => {
      window.removeEventListener('scroll', closeAccountMenu)
    }
  }, [accountMenuActive])

  const checkVisible = () => {
    const currentScroll = window.scrollY
    const offset = ((document.querySelector('.swiper')?.clientHeight / 2) || 0)

    if (currentScroll > offset && heroInView) {
      setHeroInView(false)
    }

    if (currentScroll < offset && !heroInView) {
      setHeroInView(true)
    }
  }

  useEffect(() => {
    if (!playerHeadReducer.activeState) {
      closeMenu()
      props.closeSearch()
    }
  }, [playerHeadReducer])
  const onBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      closeMenu()
      props.closeSearch()
    }
  }

  // disable body scrolling when mobile menu open
  useEffect(() => {
    if (state.menuIsActive || props.programmeSearchIsActive) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = null
    }
  }, [state.menuIsActive, props.programmeSearchIsActive])

  // Helper function to toggle search and reduce duplication
  function toggleSearchIfOverlayClassClicked(e) {
    e.preventDefault()
    const checkForClasses = ['overlay-container', 'sui-search-box__wrapper']
    // check if e.target.className includes overlay-container or sui-search-box__wrapper
    if (checkForClasses.some(c => e.target.className.includes(c))) {
      props.toggleSearch()
    }
  }

  useEffect(() => {
    if (props.programmeSearchIsActive === false) {
      return () => {}
    }
    const overlay = document?.getElementById('overlay-container')
    // add a click event listener to the div
    overlay?.addEventListener('click', toggleSearchIfOverlayClassClicked)
    return () => overlay?.removeEventListener('click', toggleSearchIfOverlayClassClicked)
  }, [props.programmeSearchIsActive])

  const handleMenuClick = (e) => {
    dispatch(setToggleState(true))
    e.preventDefault()
    props.closeSearch()
    toggleMenu()
  }

  const toggleMenu = () => setState(prevState => ({
    menuIsActive: !prevState.menuIsActive,
  }))

  const closeMenu = () => setState({ menuIsActive: false })

  const handleSearchClick = (e) => {
    dispatch(setToggleState(true))
    e.preventDefault()
    closeMenu()
    props.toggleSearch()
  }

  const getHeaderClasses = () => {
    const classes = []
    if (!heroInView) classes.push('hero-invisible')
    if (accountMenuActive) classes.push('account-menu-active')
    return classes.join(' ')
  }

  const { sponsor } = props
  const hideSponsor = useSelector(selectorState => selectorState?.playerHeader?.hideSponsor)
  return (
    <div id="player-header" className={`PlayerHeader ${getHeaderClasses()}`}>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(scriptJson) }}
      />
      <div className="LogoWrap">
        <StvLogo />
        {/* Sponsor logo only renders if loading is not null */}
        {props.isLoading !== null
          && sponsor
          && !props.currentlySubscribed
          && !hideSponsor
          && <SponsorLogo />}
      </div>
      {(
        <>
          <nav
            className="nav"
            onBlur={onBlur}
          >
            <PlayerMenu
              id="player-nav-menu"
              isActive={state.menuIsActive}
              groupToken={props.groupToken}
              closeMenu={closeMenu}
              closeSearch={props.closeSearch}
              items={menuItems}
              hostname={props.hostname}
              activeItem={activePage}
              isSearchMenuActive={props.programmeSearchIsActive}
              renderCTVBanner={props.renderCTVBanner}
              accountMenuActive={accountMenuActive}
              user={{
                firstName: props.firstName,
                lastName: props.lastName,
                signedIn: !!props.firstName,
                targetedAdvertising: props.targetedAdvertising,
                vipOptIn: props.vipOptIn,
                currentlySubscribed: props.currentlySubscribed,
              }}
            />
          </nav>
          <div className="settings-menu">
            {!props.currentlySubscribed && props.loginEnabled && props.isLoading !== null && (
              <div className="player-plus">
                <a href={`/player-plus?cid=int_stv_em_el_ctahpsub`} className="player-plus-link" onClick={() => { trackEvent('PplusNavUpgrade', { pplus_nav_upgrade: true }) }}>
                  Subscribe to <strong>STV Player+</strong>
                </a>
              </div>
            )}
            <div className="swap-icons">
              <div className="SearchLink">
                {props.programmeSearchIsActive ? (
                  <CloseSearchLink
                    active
                    text=""
                    className="search"
                    href="#player-close-search"
                    onClick={handleSearchClick}
                  />
                ) : (
                  <SearchLink
                    text=""
                    className="search"
                    href="#player-search"
                    onClick={handleSearchClick}
                    width={25}
                    height={24}
                  />
                )}
              </div>
              <div className="login-link">
                <div className={`avatar-outer ${(props.isLoading !== null) ? '' : 'loading'}`}>
                  <div className={`avatar-container`}>
                    <Avatar userInitial="" />
                  </div>
                </div>

                {props.isLoading !== null && (
                  <Login
                    currentUrl={props.currentUrl}
                    currentDomain={props.currentDomain}
                    loginService={props.loginService}
                    setAccountMenuActive={setAccountMenuActive}
                    accountMenuActive={accountMenuActive}
                  />
                )}
              </div>
            </div>
            <HamburgerLink
              href="#player-nav-menu"
              className={`nav-toggle ${props.isLoggedIn ? 'logged-in' : 'logged-out'}`}
              active={state.menuIsActive}
              onClick={handleMenuClick}
            />
          </div>
        </>
      )}
      <style jsx>{styles}</style>
    </div>
  )
}

PlayerHeader.propTypes = {
  groupToken: PropTypes.string,
  programmeSearchIsActive: PropTypes.bool.isRequired,
  closeSearch: PropTypes.func.isRequired,
  toggleSearch: PropTypes.func.isRequired,
  hostname: PropTypes.string.isRequired,
  sponsor: PropTypes.bool,
  currentlySubscribed: PropTypes.bool,
  isLoading: PropTypes.bool,
  currentUrl: PropTypes.string.isRequired,
  currentDomain: PropTypes.string.isRequired,
  loginService: PropTypes.shape({
    logOut: PropTypes.func,
  }).isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  isLoggedIn: PropTypes.bool.isRequired,
  targetedAdvertising: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  renderCTVBanner: PropTypes.bool,
  loginEnabled: PropTypes.bool,
}

PlayerHeader.defaultProps = {
  groupToken: '0071',
  sponsor: true,
  currentlySubscribed: false,
  firstName: null,
  lastName: null,
  targetedAdvertising: false,
  vipOptIn: false,
  renderCTVBanner: false,
  loginEnabled: true,
  isLoading: true,
}
export default PlayerHeader
